import {KeyboardEvent, ReactNode, useEffect, useRef, useState} from 'react';
import dayjs from 'dayjs';
import {observer} from 'mobx-react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {FiCheck, FiChevronDown} from 'react-icons/fi';
import {Avatar} from '@ui/atoms/avatar';
import {JumpIcon, NestedViewIcon} from '@ui/atoms/icons';
import {ChatVectorIcon} from '@ui/atoms/icons/chat-icon';
import {Body1, Body2, Headline2} from '@ui/atoms/typography';
import {VerticalSpacer} from '@ui/atoms/spacer';
import {ItemLoader} from '@ui/organisms/item-loader';
import {HoverTooltip} from '@ui/molecules/hover-tooltip';
import {theme} from '@ui/style/theme';
import {goalStatusBarColors} from '@ui/style/goal-status';
import {useViewInsight} from '@hooks/insights';
import {
  DashboardMember,
  DashboardPeriod,
  FeatureAvailability,
  FeedbackSignal,
  GoalHealth,
  GoalSignal,
  ManagerDashboardView,
  ManagerSignal,
  ProgramSignal,
} from './manager-dashboard.types';
import {useManagerDashboard} from './use-manager-dashboard';
import {
  Distribution,
  DistributionSegment,
  EmptyState,
  FeedbackDetailEmpty,
  FeedbackDetailHeader,
  FeedbackDetailPanel,
  FeedbackDetailRow,
  FeedbackSection,
  FeedbackSectionHeading,
  FeedbackStatCard,
  FeedbackStatGrid,
  FeedbackStatNote,
  GoalAttentionBar,
  GoalAttentionHeader,
  GoalAttentionIdentity,
  GoalAttentionPanel,
  GoalAttentionPercent,
  GoalAttentionProgress,
  GoalAttentionRow,
  GoalAttentionStatus,
  GoalAttentionTrack,
  GoalDistributionContent,
  GoalDistributionLegend,
  GoalDistributionLegendItem,
  GoalDistributionPanel,
  GoalPanelTitle,
  GoalSection,
  GoalSectionHeading,
  GoalStatCard,
  GoalStatDelta,
  GoalStatGrid,
  Intro,
  IntroCopy,
  LoaderBox,
  ManagerDashboardContent,
  Page,
  Panel,
  PersonCell,
  PersonBreadcrumb,
  PersonDetailLayout,
  PersonFeedbackGrid,
  PersonIdentity,
  PersonNavigation,
  PersonParticipationHeader,
  PersonParticipationIdentity,
  PersonParticipationPanel,
  PersonParticipationRow,
  PersonParticipationStatus,
  PersonSection,
  CheckinSection,
  CheckinSectionHeader,
  CheckinSectionHeading,
  ProgramAverageCard,
  ProgramAverageDelta,
  ProgramCadence,
  ProgramInsightGrid,
  ProgramOutstandingEmpty,
  ProgramOutstandingHeader,
  ProgramOutstandingIdentity,
  ProgramOutstandingPanel,
  ProgramOutstandingRow,
  ProgramOutstandingStatus,
  ProgramSelector,
  ProgramSelectorButton,
  ProgramSelectorMenu,
  ProgramSelectorOption,
  ProgramStatusDot,
  ProgramSummaryGrid,
  ProgramSummaryItem,
  ProgramSummaryPanel,
  ProgramTrendPanel,
  ProgramTrendTitle,
  RetryButton,
  SignalSummary,
  TableScroll,
  TeamTable,
  TeamMapCheckins,
  TeamMapHeader,
  TeamMapPanel,
  TeamMapStatus,
  TeamMapTitle,
  TeamMapWarning,
  Trend,
  TrendBar,
  TrendBarContainer,
  TrendItem,
} from './manager-dashboard.styles';
import {ManagerDashboardFilters} from './manager-dashboard-filters';
import {
  checkinSubmissionStatusLabel,
  getCheckinSubmissionStatus,
  getManagedSubmissionUserId,
  submissionDaysOverdue,
} from '@pages/dashboard/checkins/team-management/team-management.utils';

const statusLabels: Record<GoalHealth, string> = {
  behind: 'Behind',
  at_risk: 'At risk',
  no_status: 'No status',
  on_track: 'On track',
  completed: 'Ended',
};

const viewFromPath = (pathname: string): ManagerDashboardView => {
  const suffix = pathname
    .replace('/reporting/overview', '')
    .split('/')
    .filter(Boolean);
  if (suffix[0] === 'people') return 'person';
  return 'overview';
};

const FeatureState = ({
  availability,
  empty,
  emptyTitle,
  emptyCopy,
  onRetry,
  children,
}: {
  availability: FeatureAvailability;
  empty: boolean;
  emptyTitle: string;
  emptyCopy: string;
  onRetry: () => void;
  children: ReactNode;
}) => {
  if (!availability.enabled) {
    return (
      <Panel>
        <EmptyState>
          <strong>Feature unavailable</strong>
          This feature is not enabled for this workspace.
        </EmptyState>
      </Panel>
    );
  }
  if (availability.loading) {
    return (
      <Panel aria-busy="true">
        <LoaderBox>
          <ItemLoader />
        </LoaderBox>
      </Panel>
    );
  }
  if (availability.failed) {
    return (
      <Panel>
        <EmptyState role="alert">
          <strong>We couldn’t load this report</strong>
          The rest of the dashboard is still available.
          <div>
            <RetryButton type="button" onClick={onRetry}>
              Try again
            </RetryButton>
          </div>
        </EmptyState>
      </Panel>
    );
  }
  if (empty) {
    return (
      <Panel>
        <EmptyState>
          <strong>{emptyTitle}</strong>
          {emptyCopy}
        </EmptyState>
      </Panel>
    );
  }
  return <>{children}</>;
};

const DashboardAvatar = ({member}: {member: any}) => (
  <Avatar
    name={member.name}
    userId={member.id}
    src={member.avatar}
    size="md"
    tooltip={false}
    customUsers={[member.raw]}
    timezone={member.timezone}
    alt=""
  />
);

const signalTitle = (signal: ManagerSignal) => {
  if (signal.kind === 'goal-behind') return 'Goal behind';
  if (signal.kind === 'program-overdue') return 'Check-in overdue';
  if (signal.kind === 'goal-risk') return 'Goal at risk';
  return 'Feedback requested';
};

const TeamSignal = ({signals}: {signals: ManagerSignal[]}) => {
  const signal = signals[0];
  if (!signal) {
    return (
      <SignalSummary>
        <strong>No priority signal</strong>
        <small>Nothing currently requires attention</small>
      </SignalSummary>
    );
  }
  const more = signals.length - 1;
  return (
    <SignalSummary>
      <strong>{signalTitle(signal)}</strong>
      <small>
        {signal.detail} · {signal.label}
        {more > 0 ? ` · +${more} more` : ''}
      </small>
    </SignalSummary>
  );
};

const goalCounts = (goals: GoalSignal[]) => ({
  total: goals.length,
  on_track: goals.filter((goal) => goal.status === 'on_track').length,
  at_risk: goals.filter((goal) => goal.status === 'at_risk').length,
  behind: goals.filter((goal) => goal.status === 'behind').length,
  completed: goals.filter((goal) => goal.status === 'completed').length,
  no_status: goals.filter((goal) => goal.status === 'no_status').length,
});

export const GoalStat = ({
  label,
  value,
  change,
  lowerIsBetter = false,
}: {
  label: string;
  value: number;
  change?: number;
  lowerIsBetter?: boolean;
}) => {
  const isImprovement =
    change !== undefined && (lowerIsBetter ? change < 0 : change > 0);
  const direction =
    change === undefined || change === 0
      ? 'neutral'
      : isImprovement
      ? 'positive'
      : 'negative';

  return (
    <GoalStatCard>
      <span>{label}</span>
      <strong>{value}</strong>
      <GoalStatDelta $direction={direction}>
        {change === undefined ? (
          <>&nbsp;</>
        ) : (
          <>
            <em>
              {change > 0 ? '+' : ''}
              {change}
            </em>{' '}
            vs previous period
          </>
        )}
      </GoalStatDelta>
    </GoalStatCard>
  );
};

const GoalsDetail = ({
  goals,
  previousGoals,
  availability,
  onRetry,
  showOwner = true,
}: {
  goals: GoalSignal[];
  previousGoals: GoalSignal[];
  availability: FeatureAvailability;
  onRetry: () => void;
  showOwner?: boolean;
}) => {
  const viewGoalInsight = useViewInsight();
  const counts = goalCounts(goals);
  const previous = goalCounts(previousGoals);
  const distribution = [
    {
      key: 'behind' as GoalHealth,
      value: counts.behind,
      color: goalStatusBarColors.behind,
      label: 'behind',
    },
    {
      key: 'at_risk' as GoalHealth,
      value: counts.at_risk,
      color: goalStatusBarColors.at_risk,
      label: 'at risk',
    },
    {
      key: 'on_track' as GoalHealth,
      value: counts.on_track,
      color: goalStatusBarColors.on_track,
      label: 'on track',
    },
  ];
  const healthTotal = distribution.reduce(
    (total, item) => total + item.value,
    0,
  );
  const attention = goals.filter((goal) =>
    ['behind', 'at_risk'].includes(goal.status),
  );

  return (
    <FeatureState
      availability={availability}
      empty={!goals.length}
      emptyTitle="No goals in this period"
      emptyCopy="Goals created during the selected period will appear here."
      onRetry={onRetry}>
      <GoalStatGrid>
        <GoalStat
          label="Total"
          value={counts.total}
          change={counts.total - previous.total}
        />
        <GoalStat
          label="On track"
          value={counts.on_track}
          change={counts.on_track - previous.on_track}
        />
        <GoalStat
          label="At risk"
          value={counts.at_risk}
          change={counts.at_risk - previous.at_risk}
          lowerIsBetter
        />
        <GoalStat
          label="Behind"
          value={counts.behind}
          change={counts.behind - previous.behind}
          lowerIsBetter
        />
        <GoalStat label="Ended" value={counts.completed} />
      </GoalStatGrid>
      <GoalDistributionPanel aria-label="Goal health summary">
        <GoalPanelTitle>Goal health this period</GoalPanelTitle>
        <GoalDistributionContent>
          <Distribution aria-label="Goals by health">
            {distribution.map((item) => (
              <DistributionSegment
                key={item.key}
                $color={item.color}
                $width={healthTotal ? (item.value / healthTotal) * 100 : 0}
                title={`${statusLabels[item.key]}: ${item.value}`}
              />
            ))}
          </Distribution>
          <GoalDistributionLegend>
            {distribution.map((item) => (
              <GoalDistributionLegendItem key={item.key} $color={item.color}>
                <span>
                  <strong>{item.value}</strong> {item.label}
                </span>
              </GoalDistributionLegendItem>
            ))}
          </GoalDistributionLegend>
        </GoalDistributionContent>
      </GoalDistributionPanel>
      <GoalAttentionPanel>
        <GoalAttentionHeader>
          <h3>Needs attention</h3>
        </GoalAttentionHeader>
        {attention.length ? (
          attention
            .sort(
              (a, b) =>
                Number(b.status === 'behind') - Number(a.status === 'behind') ||
                a.progress - b.progress,
            )
            .map((goal) => (
              <GoalAttentionRow
                key={goal.id}
                type="button"
                onClick={() =>
                  viewGoalInsight(goal.id, Boolean(goal.goal?.isKpi))
                }>
                <GoalAttentionIdentity>
                  {showOwner && (
                    <DashboardAvatar
                      member={{
                        id: goal.memberId,
                        name: goal.memberName,
                        raw: goal.goal?.assignee || {},
                      }}
                    />
                  )}
                  <div>
                    <strong>{goal.title}</strong>
                    <small>
                      {showOwner && `${goal.memberName} · `}
                      {goal.goal?.isKpi ? 'KPI' : 'OKR'}
                    </small>
                  </div>
                </GoalAttentionIdentity>
                <GoalAttentionProgress>
                  <GoalAttentionTrack aria-hidden="true">
                    <GoalAttentionBar
                      $progress={goal.progress}
                      $status={goal.status}
                    />
                  </GoalAttentionTrack>
                  <GoalAttentionPercent>{goal.progress}%</GoalAttentionPercent>
                  <GoalAttentionStatus $status={goal.status}>
                    {statusLabels[goal.status]}
                  </GoalAttentionStatus>
                </GoalAttentionProgress>
              </GoalAttentionRow>
            ))
        ) : (
          <EmptyState>
            <strong>All clear</strong>No goals need attention in this period.
          </EmptyState>
        )}
      </GoalAttentionPanel>
    </FeatureState>
  );
};

const feedbackChangeNote = (change?: number) => {
  const direction =
    change === undefined || change === 0
      ? 'neutral'
      : change > 0
      ? 'positive'
      : 'negative';

  return (
    <FeedbackStatNote $direction={direction}>
      {change === undefined ? (
        <>— vs previous period</>
      ) : (
        <>
          <em>
            {change > 0 ? '+' : ''}
            {change}
          </em>{' '}
          vs previous period
        </>
      )}
    </FeedbackStatNote>
  );
};

const PersonFeedbackDetail = ({
  feedback,
  previousFeedback,
  availability,
  onRetry,
}: {
  feedback: FeedbackSignal[];
  previousFeedback: FeedbackSignal[];
  availability: FeatureAvailability;
  onRetry: () => void;
}) => {
  const signal = feedback[0];
  const previous = previousFeedback[0];

  return (
    <FeatureState
      availability={availability}
      empty={!feedback.length}
      emptyTitle="No feedback in this period"
      emptyCopy="This person has no feedback activity in the selected period."
      onRetry={onRetry}>
      <PersonFeedbackGrid>
        <FeedbackStatCard>
          <span>Praise posted</span>
          <strong>{signal?.praise || 0}</strong>
          {feedbackChangeNote(
            previous ? (signal?.praise || 0) - previous.praise : undefined,
          )}
        </FeedbackStatCard>
        <FeedbackStatCard>
          <span>Feedback received</span>
          <strong>{signal?.substantive || 0}</strong>
          {feedbackChangeNote(
            previous
              ? (signal?.substantive || 0) - previous.substantive
              : undefined,
          )}
        </FeedbackStatCard>
        <FeedbackStatCard>
          <span>Feedback status</span>
          <strong>{signal?.substantive > 0 ? 'Covered' : 'Gap'}</strong>
          <FeedbackStatNote>
            {signal?.substantive > 0
              ? 'Substantive feedback received'
              : `${signal?.gapDays || 0} days since substantive feedback`}
          </FeedbackStatNote>
        </FeedbackStatCard>
      </PersonFeedbackGrid>
    </FeatureState>
  );
};

const TeamFeedbackSection = ({
  feedback,
  previousFeedback,
  availability,
  onRetry,
}: {
  feedback: FeedbackSignal[];
  previousFeedback: FeedbackSignal[];
  availability: FeatureAvailability;
  onRetry: () => void;
}) => {
  const praise = feedback.reduce((sum, item) => sum + item.praise, 0);
  const received = feedback.reduce((sum, item) => sum + item.substantive, 0);
  const previousPraise = previousFeedback.reduce(
    (sum, item) => sum + item.praise,
    0,
  );
  const previousReceived = previousFeedback.reduce(
    (sum, item) => sum + item.substantive,
    0,
  );
  const covered = feedback.filter((item) => item.substantive > 0).length;
  const coverage = feedback.length
    ? Math.round((covered / feedback.length) * 100)
    : 0;
  const gaps = feedback.filter((item) => item.substantive < 1);
  const comparisonReady = previousFeedback.length > 0;

  return (
    <FeatureState
      availability={availability}
      empty={!feedback.length}
      emptyTitle="No feedback in this period"
      emptyCopy="Feedback activity for the selected period will appear here."
      onRetry={onRetry}>
      <FeedbackStatGrid>
        <FeedbackStatCard>
          <span>Praise posted</span>
          <strong>{praise}</strong>
          {feedbackChangeNote(
            comparisonReady ? praise - previousPraise : undefined,
          )}
        </FeedbackStatCard>
        <FeedbackStatCard>
          <span>Feedback received</span>
          <strong>{received}</strong>
          {feedbackChangeNote(
            comparisonReady ? received - previousReceived : undefined,
          )}
        </FeedbackStatCard>
        <FeedbackStatCard>
          <span>Coverage</span>
          <strong>{coverage}%</strong>
          <FeedbackStatNote>
            {covered} of {feedback.length} received feedback
          </FeedbackStatNote>
        </FeedbackStatCard>
      </FeedbackStatGrid>
      <FeedbackDetailPanel>
        {gaps.length ? (
          <>
            <FeedbackDetailHeader>
              No feedback received in this period
            </FeedbackDetailHeader>
            {gaps.map((item) => (
              <FeedbackDetailRow key={item.memberId}>
                <DashboardAvatar
                  member={{
                    id: item.memberId,
                    name: item.memberName,
                    raw: {},
                  }}
                />
                <strong>{item.memberName}</strong>
              </FeedbackDetailRow>
            ))}
          </>
        ) : (
          <FeedbackDetailEmpty>
            Everyone in this period has received feedback.
          </FeedbackDetailEmpty>
        )}
      </FeedbackDetailPanel>
    </FeatureState>
  );
};

const PersonProgramsDetail = ({
  programs,
  personId,
  period,
  availability,
  onRetry,
}: {
  programs: ProgramSignal[];
  personId: string;
  period: DashboardPeriod;
  availability: FeatureAvailability;
  onRetry: () => void;
}) => {
  const periodStart = dayjs(period.starts).startOf('day');
  const periodEnd = dayjs(period.ends).endOf('day');
  const participation = programs
    .filter((program) => !program.anonymous)
    .flatMap((program) =>
      program.occurrences
        .filter((occurrence) => {
          const occurrenceDate = dayjs(occurrence.key);
          return (
            !occurrenceDate.isValid() ||
            (!occurrenceDate.isBefore(periodStart) &&
              !occurrenceDate.isAfter(periodEnd))
          );
        })
        .map((occurrence) => ({
          program,
          occurrence,
          submission: occurrence.submissions.find(
            (item) => getManagedSubmissionUserId(item) === personId,
          ),
        })),
    )
    .filter((item) => Boolean(item.submission))
    .sort(
      (a, b) =>
        dayjs(b.occurrence.key).valueOf() - dayjs(a.occurrence.key).valueOf(),
    )
    .slice(0, 3);

  return (
    <FeatureState
      availability={availability}
      empty={!participation.length}
      emptyTitle="No check-in cycle activity"
      emptyCopy="There is no identifiable check-in participation for this person in the selected period."
      onRetry={onRetry}>
      <PersonParticipationPanel>
        <PersonParticipationHeader>Participation</PersonParticipationHeader>
        {participation.map(({program, occurrence, submission}) => {
          const submissionStatus = getCheckinSubmissionStatus(submission, {
            isLatestOccurrence:
              occurrence.key ===
              (program.latest?.key || program.occurrences[0]?.key),
          });
          const status =
            submissionStatus.kind === 'submitted'
              ? 'submitted'
              : submissionStatus.kind === 'overdue' ||
                submissionStatus.kind === 'missed'
              ? 'overdue'
              : 'outstanding';
          return (
            <PersonParticipationRow
              key={`${program.id}-${occurrence.key}-${
                submission?.id || personId
              }`}>
              <PersonParticipationIdentity>
                <strong>{program.name}</strong>
                <small>{occurrence.label || 'Latest occurrence'}</small>
              </PersonParticipationIdentity>
              <PersonParticipationStatus $status={status}>
                {checkinSubmissionStatusLabel(submissionStatus)}
              </PersonParticipationStatus>
            </PersonParticipationRow>
          );
        })}
      </PersonParticipationPanel>
    </FeatureState>
  );
};

const ProgramsDetail = ({
  programs,
  selectedId,
  members,
  availability,
  onRetry,
  onSelect,
  personHref,
}: {
  programs: ProgramSignal[];
  selectedId: string;
  members: DashboardMember[];
  availability: FeatureAvailability;
  onRetry: () => void;
  onSelect: (id: string) => void;
  personHref: (id: string) => string;
}) => {
  const selectorRef = useRef<HTMLDivElement>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const selected =
    programs.find((program) => program.id === selectedId) || programs[0];
  const latest = selected?.latest;
  const recentOccurrences = selected?.occurrences.slice(0, 6) || [];
  const previousOccurrences = selected?.occurrences.slice(6, 12) || [];
  const average = recentOccurrences.length
    ? Math.round(
        recentOccurrences.reduce(
          (sum, occurrence) => sum + occurrence.percentage,
          0,
        ) / recentOccurrences.length,
      )
    : 0;
  const previousAverage = previousOccurrences.length
    ? Math.round(
        previousOccurrences.reduce(
          (sum, occurrence) => sum + occurrence.percentage,
          0,
        ) / previousOccurrences.length,
      )
    : null;
  const averageDelta =
    previousAverage === null ? null : average - previousAverage;
  const submissions = latest?.submissions || [];
  const excused = submissions.filter((submission) => submission?.isExcused)
    .length;
  const overdueDays = submissions.reduce(
    (highest, submission) =>
      Math.max(highest, submissionDaysOverdue(submission)),
    0,
  );
  const hasOverdueSubmissions = submissions.some(
    (submission) => getCheckinSubmissionStatus(submission).kind === 'overdue',
  );
  const outstandingPeople = selected?.anonymous
    ? []
    : submissions
        .map((submission) => ({
          submission,
          member: members.find(
            (member) => member.id === getManagedSubmissionUserId(submission),
          ),
          status: getCheckinSubmissionStatus(submission),
        }))
        .filter(
          (item): item is typeof item & {member: DashboardMember} =>
            Boolean(item.member) &&
            (item.status.kind === 'pending' || item.status.kind === 'overdue'),
        );
  const datedSubmission = submissions.find(
    (submission) =>
      submission?.dueDate || submission?.dueAt || submission?.dueDay,
  );
  const dueValue =
    datedSubmission?.dueDate ||
    datedSubmission?.dueAt ||
    datedSubmission?.dueDay ||
    selected?.cycle?.dueDate ||
    selected?.cycle?.dueDay;
  const dueDate =
    dueValue && dayjs(dueValue).isValid()
      ? dayjs(dueValue).format('DD MMM YYYY')
      : dueValue || '—';
  const cadenceConfig = selected?.cycle?.config || selected?.cycle || {};
  const cadence = String(
    cadenceConfig.frequency || selected?.cycle?.frequency || 'Recurring',
  )
    .replace(/-/g, ' ')
    .replace(/^./, (value) => value.toUpperCase());
  const cadenceDays = Array.isArray(cadenceConfig.dayOfWeek)
    ? cadenceConfig.dayOfWeek
        .map((day: string) =>
          String(day).replace(/^./, (value) => value.toUpperCase()),
        )
        .join(', ')
    : cadenceConfig.dayOfWeek || selected?.cycle?.dueDay;
  const isDaily = cadence.toLowerCase() === 'daily';
  const selectedHasOutstanding = Boolean(latest?.pending);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!selectorRef.current?.contains(event.target as Node)) {
        setSelectorOpen(false);
      }
    };
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setSelectorOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const selectProgram = (id: string) => {
    onSelect(id);
    setSelectorOpen(false);
  };

  const occurrenceLabel = (key: string, isLatest: boolean) => {
    if (isLatest) return 'Now';
    return dayjs(key).isValid() ? dayjs(key).format('D MMM') : key;
  };

  return (
    <FeatureState
      availability={availability}
      empty={!programs.length}
      emptyTitle="No check-in cycles"
      emptyCopy="Check-in cycles with submissions for this scope will appear here."
      onRetry={onRetry}>
      {selected && (
        <>
          <ProgramSelector ref={selectorRef}>
            <ProgramSelectorButton
              type="button"
              $open={selectorOpen}
              aria-label={`Select check-in cycle. ${selected.name}. ${
                selectedHasOutstanding
                  ? 'Outstanding submissions'
                  : 'No outstanding submissions'
              }`}
              aria-haspopup="listbox"
              aria-expanded={selectorOpen}
              onClick={() => setSelectorOpen((current) => !current)}>
              <span>
                <ProgramStatusDot $hasOutstanding={selectedHasOutstanding} />
                {selected.name}
              </span>
              <FiChevronDown size={16} aria-hidden="true" />
            </ProgramSelectorButton>
            {selectorOpen && (
              <ProgramSelectorMenu
                role="listbox"
                aria-label="Check-in cycle options">
                {programs.map((program) => {
                  const hasOutstanding = Boolean(program.latest?.pending);
                  return (
                    <ProgramSelectorOption
                      key={program.id}
                      type="button"
                      role="option"
                      $active={selected.id === program.id}
                      aria-selected={selected.id === program.id}
                      aria-label={`${program.name}, ${
                        hasOutstanding
                          ? 'outstanding submissions'
                          : 'no outstanding submissions'
                      }`}
                      onClick={() => selectProgram(program.id)}>
                      <span>
                        <ProgramStatusDot $hasOutstanding={hasOutstanding} />
                        {program.name}
                      </span>
                      {selected.id === program.id && (
                        <FiCheck size={16} aria-hidden="true" />
                      )}
                    </ProgramSelectorOption>
                  );
                })}
              </ProgramSelectorMenu>
            )}
          </ProgramSelector>
          <ProgramSummaryPanel>
            <ProgramSummaryGrid>
              <ProgramSummaryItem>
                <span>Cycle</span>
                <strong>{selected.name}</strong>
                {isDaily && cadenceDays ? (
                  <HoverTooltip
                    tooltipText={cadenceDays}
                    tooltipStyle={{
                      top: '24px',
                      right: 'auto',
                      left: 0,
                      zIndex: 50,
                      width: '240px',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      boxShadow: '0 3px 12px rgba(30, 30, 47, 0.2)',
                    }}
                    tooltipTextStyle={{
                      whiteSpace: 'normal',
                      lineHeight: '18px',
                    }}
                    showHoverBackground={false}>
                    <ProgramCadence tabIndex={0}>Daily</ProgramCadence>
                  </HoverTooltip>
                ) : (
                  <ProgramCadence>
                    {cadence}
                    {cadenceDays ? ` · ${cadenceDays}` : ''}
                  </ProgramCadence>
                )}
              </ProgramSummaryItem>
              <ProgramSummaryItem>
                <span>This occurrence</span>
                <strong>{latest?.percentage || 0}%</strong>
                <small>
                  {latest?.submitted || 0} of {latest?.total || 0} submitted
                </small>
              </ProgramSummaryItem>
              <ProgramSummaryItem>
                <span>Due date</span>
                <strong>{dueDate}</strong>
                <small>
                  {overdueDays
                    ? `${overdueDays} day${
                        overdueDays === 1 ? '' : 's'
                      } overdue`
                    : hasOverdueSubmissions
                    ? 'Overdue'
                    : 'On schedule'}
                </small>
              </ProgramSummaryItem>
              <ProgramSummaryItem>
                <span>Outstanding</span>
                <strong>{latest?.pending || 0}</strong>
                <small>{excused} excused</small>
              </ProgramSummaryItem>
            </ProgramSummaryGrid>
          </ProgramSummaryPanel>
          <ProgramInsightGrid>
            <ProgramAverageCard>
              <span>Average · last six occurrences</span>
              <strong>{average}%</strong>
              <ProgramAverageDelta
                $direction={
                  averageDelta === null || averageDelta === 0
                    ? 'neutral'
                    : averageDelta > 0
                    ? 'positive'
                    : 'negative'
                }>
                {averageDelta === null
                  ? '—'
                  : `${averageDelta > 0 ? '+' : ''}${averageDelta}%`}{' '}
                <span>vs previous period</span>
              </ProgramAverageDelta>
            </ProgramAverageCard>
            <ProgramTrendPanel>
              <ProgramTrendTitle>
                Completion trend · Last six occurrences
              </ProgramTrendTitle>
              <Trend>
                {[...recentOccurrences]
                  .reverse()
                  .map((occurrence, index, occurrences) => {
                    const isLatest = index === occurrences.length - 1;
                    return (
                      <TrendItem key={occurrence.key}>
                        <TrendBarContainer>
                          <HoverTooltip
                            tooltipText={`${occurrence.percentage}%`}
                            tooltipStyle={{
                              top: 'auto',
                              right: 'auto',
                              bottom: 'calc(100% + 6px)',
                              left: '50%',
                              zIndex: 60,
                              width: 'max-content',
                              padding: '4px 8px',
                              transform: 'translateX(-50%)',
                              pointerEvents: 'none',
                            }}
                            showHoverBackground={false}>
                            <TrendBar
                              $height={occurrence.percentage}
                              $active={isLatest}
                              role="img"
                              tabIndex={0}
                              aria-label={`${occurrence.label}, ${occurrence.percentage}% completion`}
                            />
                          </HoverTooltip>
                        </TrendBarContainer>
                        <span>{occurrenceLabel(occurrence.key, isLatest)}</span>
                      </TrendItem>
                    );
                  })}
              </Trend>
            </ProgramTrendPanel>
          </ProgramInsightGrid>
          <ProgramOutstandingPanel>
            <ProgramOutstandingHeader>
              Outstanding · {selected.name}
            </ProgramOutstandingHeader>
            {selected.anonymous ? (
              <ProgramOutstandingEmpty>
                <Body2 kind="textBody">
                  Individual submission status is not shown for anonymous
                  check-in cycles.
                </Body2>
              </ProgramOutstandingEmpty>
            ) : outstandingPeople.length ? (
              outstandingPeople.map(({member, submission, status}) => (
                <ProgramOutstandingRow
                  key={submission.id || member.id}
                  to={personHref(member.id)}>
                  <ProgramOutstandingIdentity>
                    <DashboardAvatar member={member} />
                    <div>
                      <strong>{member.name}</strong>
                      <small>Due {dueDate}</small>
                    </div>
                  </ProgramOutstandingIdentity>
                  <ProgramOutstandingStatus>
                    {checkinSubmissionStatusLabel(status)}
                  </ProgramOutstandingStatus>
                </ProgramOutstandingRow>
              ))
            ) : (
              <ProgramOutstandingEmpty>
                <Body1 kind="textBody">
                  All eligible participants have submitted.
                </Body1>
              </ProgramOutstandingEmpty>
            )}
          </ProgramOutstandingPanel>
        </>
      )}
    </FeatureState>
  );
};

export const ManagerDashboard = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const view = viewFromPath(location.pathname);
  const personId =
    view === 'person' ? location.pathname.split('/').filter(Boolean).pop() : '';
  const {
    model,
    previousGoals,
    previousFeedback,
    scopes,
    scope,
    period,
    availability,
    selectedProgramId,
    rosterLoading,
    rosterFailed,
    updateParam,
    refetchFailed,
  } = useManagerDashboard();

  const query = searchParams.toString();
  const dashboardHref = (path: string) => `${path}${query ? `?${query}` : ''}`;
  const navigateView = (path: string) => navigate(dashboardHref(path));

  const partialFailure = Object.values(availability).some(
    (feature) => feature.enabled && feature.failed,
  );
  const dashboardLoading = Object.values(availability).some(
    (feature) => feature.enabled && feature.loading,
  );

  useEffect(() => {
    const sectionId = location.hash.replace('#', '');
    if (
      view !== 'overview' ||
      !['goals', 'programs', 'feedback'].includes(sectionId) ||
      dashboardLoading
    ) {
      return;
    }
    document.getElementById(sectionId)?.scrollIntoView();
  }, [dashboardLoading, location.hash, view]);

  const handleTeamKey = (
    event: KeyboardEvent<HTMLTableRowElement>,
    memberId: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigateView(`/reporting/overview/people/${memberId}`);
    }
  };

  const selectedPerson = model.members.find((member) => member.id === personId);
  const selectedPersonRole =
    selectedPerson?.jobTitle || selectedPerson?.email || 'Team member';
  const selectedPersonGroups = selectedPerson?.groups || [];
  const personGoals = model.goals.filter((goal) => goal.memberId === personId);
  const personFeedback = model.feedback.filter(
    (item) => item.memberId === personId,
  );
  const previousPersonFeedback = previousFeedback.filter(
    (item) => item.memberId === personId,
  );

  return (
    <Page>
      <ManagerDashboardContent width="wide" $personView={view === 'person'}>
        {view === 'overview' ? (
          <Intro>
            <IntroCopy>
              <Headline2>Overview</Headline2>
              <VerticalSpacer size="4px" />
              <Body1 kind="textBody">
                A focused view of the signals that need your attention.
              </Body1>
            </IntroCopy>
            <ManagerDashboardFilters
              scopes={scopes}
              scope={scope}
              period={period}
              onScopeChange={(scopeId) => updateParam('scope', scopeId)}
              onPeriodChange={(periodId) => updateParam('period', periodId)}
            />
          </Intro>
        ) : selectedPerson ? (
          <PersonNavigation>
            <PersonBreadcrumb aria-label="Breadcrumb">
              <Link to={dashboardHref('/reporting/overview')}>Overview</Link>
              <span aria-hidden="true">&gt;</span>
              <span>{selectedPerson.name}</span>
            </PersonBreadcrumb>
            <ManagerDashboardFilters
              scopes={scopes}
              scope={scope}
              period={period}
              members={model.members}
              person={selectedPerson}
              onPersonChange={(nextPersonId) =>
                navigateView(`/reporting/overview/people/${nextPersonId}`)
              }
              onScopeChange={(scopeId) => updateParam('scope', scopeId)}
              onPeriodChange={(periodId) => updateParam('period', periodId)}
            />
          </PersonNavigation>
        ) : null}

        {rosterLoading ? (
          <Panel aria-busy="true">
            <LoaderBox>
              <ItemLoader />
            </LoaderBox>
          </Panel>
        ) : rosterFailed ? (
          <Panel>
            <EmptyState role="alert">
              <strong>We couldn’t load this team</strong>Try the request again.
              <div>
                <RetryButton type="button" onClick={refetchFailed}>
                  Try again
                </RetryButton>
              </div>
            </EmptyState>
          </Panel>
        ) : !model.members.length ? (
          <Panel>
            <EmptyState>
              <strong>Nothing here yet</strong>Select another group or add
              direct reports to view manager insights.
            </EmptyState>
          </Panel>
        ) : view === 'overview' && dashboardLoading ? (
          <Panel aria-busy="true">
            <LoaderBox>
              <ItemLoader />
            </LoaderBox>
          </Panel>
        ) : view === 'overview' ? (
          <>
            <TeamMapPanel>
              <TeamMapHeader>
                <TeamMapTitle>Team map</TeamMapTitle>
                {partialFailure && (
                  <TeamMapWarning role="status">
                    Some data could not be loaded
                  </TeamMapWarning>
                )}
              </TeamMapHeader>
              <TableScroll>
                <TeamTable>
                  <colgroup>
                    <col />
                    <col />
                    <col />
                    <col />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Person</th>
                      <th>Signal</th>
                      <th>Goals</th>
                      <th>Feedback</th>
                      <th>Check-ins</th>
                    </tr>
                  </thead>
                  <tbody>
                    {model.teamMap.map((row) => (
                      <tr
                        key={row.member.id}
                        tabIndex={0}
                        onClick={() =>
                          navigateView(
                            `/reporting/overview/people/${row.member.id}`,
                          )
                        }
                        onKeyDown={(event) =>
                          handleTeamKey(event, row.member.id)
                        }>
                        <td data-label="Person">
                          <PersonCell>
                            <DashboardAvatar member={row.member} />
                            <div>
                              <strong>{row.member.name}</strong>
                              <small>
                                {row.member.jobTitle ||
                                  row.member.email ||
                                  'Team member'}
                              </small>
                            </div>
                          </PersonCell>
                        </td>
                        <td data-label="Signal">
                          <TeamSignal signals={row.signals} />
                        </td>
                        <td data-label="Goals">
                          {row.worstGoal ? (
                            <TeamMapStatus $status={row.worstGoal}>
                              {statusLabels[row.worstGoal]} · {row.goals.length}{' '}
                              goal{row.goals.length === 1 ? '' : 's'}
                            </TeamMapStatus>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td data-label="Feedback">
                          {row.feedback ? (
                            <TeamMapStatus
                              $status={
                                row.feedback.substantive > 0 ? 'covered' : 'gap'
                              }>
                              {row.feedback.substantive > 0
                                ? 'Covered'
                                : 'No feedback'}
                            </TeamMapStatus>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td data-label="Check-ins">
                          {row.submittedPrograms || row.overduePrograms ? (
                            <TeamMapCheckins>
                              <strong>{row.submittedPrograms} submitted</strong>
                              <span>{row.overduePrograms} overdue</span>
                            </TeamMapCheckins>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TeamTable>
              </TableScroll>
            </TeamMapPanel>
            <GoalSection id="goals">
              <GoalSectionHeading>
                <NestedViewIcon width={18} height={18} aria-hidden="true" />
                <h2>Goals</h2>
              </GoalSectionHeading>
              <GoalsDetail
                goals={model.goals}
                previousGoals={previousGoals}
                availability={availability.goals}
                onRetry={refetchFailed}
              />
            </GoalSection>
            <CheckinSection id="programs">
              <CheckinSectionHeader>
                <CheckinSectionHeading>
                  <JumpIcon
                    width={18}
                    height={18}
                    color={theme.colors.purple300}
                    aria-hidden="true"
                  />
                  <h2>Check-in cycles</h2>
                </CheckinSectionHeading>
              </CheckinSectionHeader>
              <ProgramsDetail
                programs={model.programs}
                selectedId={selectedProgramId}
                members={model.members}
                availability={availability.programs}
                onRetry={refetchFailed}
                onSelect={(id) => updateParam('program', id)}
                personHref={(id) =>
                  dashboardHref(`/reporting/overview/people/${id}`)
                }
              />
            </CheckinSection>
            <FeedbackSection id="feedback" aria-labelledby="feedback-heading">
              <FeedbackSectionHeading>
                <ChatVectorIcon
                  width={18}
                  height={18}
                  color={theme.colors.purple300}
                  aria-hidden="true"
                />
                <h2 id="feedback-heading">Feedback</h2>
              </FeedbackSectionHeading>
              <TeamFeedbackSection
                feedback={model.feedback}
                previousFeedback={previousFeedback}
                availability={availability.feedback}
                onRetry={refetchFailed}
              />
            </FeedbackSection>
          </>
        ) : selectedPerson ? (
          <PersonDetailLayout>
            <PersonIdentity>
              <h1>{selectedPerson.name}</h1>
              <p
                aria-label={`${selectedPersonRole}${
                  selectedPersonGroups.length
                    ? ` · Groups: ${selectedPersonGroups.join(', ')}`
                    : ''
                }`}>
                {selectedPersonRole}
                {Boolean(selectedPersonGroups.length) && (
                  <>
                    {' · '}
                    <strong>Groups:</strong> {selectedPersonGroups.join(', ')}
                  </>
                )}
              </p>
            </PersonIdentity>
            <PersonSection aria-labelledby="person-goals-heading">
              <GoalSectionHeading>
                <NestedViewIcon aria-hidden="true" />
                <h2 id="person-goals-heading">Goals</h2>
              </GoalSectionHeading>
              <GoalsDetail
                goals={personGoals}
                previousGoals={previousGoals.filter(
                  (goal) => goal.memberId === personId,
                )}
                availability={availability.goals}
                onRetry={refetchFailed}
                showOwner={false}
              />
            </PersonSection>
            <PersonSection aria-labelledby="person-checkins-heading">
              <CheckinSectionHeading>
                <JumpIcon
                  width={18}
                  height={18}
                  color={theme.colors.purple300}
                  aria-hidden="true"
                />
                <h2 id="person-checkins-heading">Check-in cycles</h2>
              </CheckinSectionHeading>
              <PersonProgramsDetail
                programs={model.programs}
                personId={personId || ''}
                period={period}
                availability={availability.programs}
                onRetry={refetchFailed}
              />
            </PersonSection>
            <PersonSection aria-labelledby="person-feedback-heading">
              <FeedbackSectionHeading>
                <ChatVectorIcon
                  width={18}
                  height={18}
                  color={theme.colors.purple300}
                  aria-hidden="true"
                />
                <h2 id="person-feedback-heading">Feedback</h2>
              </FeedbackSectionHeading>
              <PersonFeedbackDetail
                feedback={personFeedback}
                previousFeedback={previousPersonFeedback}
                availability={availability.feedback}
                onRetry={refetchFailed}
              />
            </PersonSection>
          </PersonDetailLayout>
        ) : (
          <Panel>
            <EmptyState>
              <strong>Person not found</strong>This person is not part of the
              selected scope.
            </EmptyState>
          </Panel>
        )}
      </ManagerDashboardContent>
    </Page>
  );
});
